/**
 * Flavor Dot Image Swap Feature
 * Handles clicking flavor dots on product cards to update images, prices, badges, and URLs
 */

(function () {
  "use strict";

  // Configuration
  const TRANSITION_DURATION = 200; // ms
  const FADE_CLASS = "fade-transition";

  /**
   * Main FlavorDotSwap class
   */
  class FlavorDotSwap {
    constructor() {
      this.init();
    }

    init() {
      // Use event delegation for efficiency
      document.addEventListener("click", this.handleDotClick.bind(this));
      document.addEventListener("keydown", this.handleDotKeydown.bind(this));

      // Preload variant images on hover for better UX
      document.addEventListener("mouseenter", this.handleDotHover.bind(this), true);
    }

    /**
     * Handle click on flavor dot
     */
    handleDotClick(event) {
      const dot = event.target.closest(".flavor-dot");
      if (!dot) return;

      event.preventDefault();
      event.stopPropagation();

      const productCard = dot.closest(".grid-product");
      if (!productCard) return;

      this.updateProductCard(productCard, dot);
    }

    /**
     * Handle keyboard interaction on flavor dot
     */
    handleDotKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;

      const dot = event.target.closest(".flavor-dot");
      if (!dot) return;

      event.preventDefault();
      event.stopPropagation();

      const productCard = dot.closest(".grid-product");
      if (!productCard) return;

      this.updateProductCard(productCard, dot);
    }

    /**
     * Handle hover on flavor dot (preload images)
     */
    handleDotHover(event) {
      const tgt = event && event.target;
      // Some mouseenter targets can be non-Element nodes; guard before using closest
      const dot =
        tgt && typeof tgt.closest === "function"
          ? tgt.closest(".flavor-dot")
          : tgt && tgt.parentElement && typeof tgt.parentElement.closest === "function"
          ? tgt.parentElement.closest(".flavor-dot")
          : null;
      if (!dot) return;

      const variantImageUrl = dot.getAttribute("data-variant-image");
      if (!variantImageUrl) return;

      // Preload the variant image
      const img = new Image();
      img.src = variantImageUrl;
    }

    /**
     * Update product card with variant data
     */
    updateProductCard(productCard, selectedDot) {
      const variantId = selectedDot.getAttribute("data-variant-id");
      if (!variantId) return;

      // Update dot states
      this.updateDotStates(productCard, selectedDot);

      // Update images
      this.updateImages(productCard, selectedDot);

      // Update price
      this.updatePrice(productCard, selectedDot);

      // Update badges
      this.updateBadges(productCard, selectedDot);

      // Update product link URL
      this.updateProductUrl(productCard, selectedDot);

      // Store selected variant
      productCard.setAttribute("data-selected-variant-id", variantId);
    }

    /**
     * Update active state of flavor dots
     */
    updateDotStates(productCard, selectedDot) {
      const allDots = productCard.querySelectorAll(".flavor-dot");

      allDots.forEach((dot) => {
        if (dot === selectedDot) {
          dot.setAttribute("aria-pressed", "true");
        } else {
          dot.setAttribute("aria-pressed", "false");
        }
      });
    }

    /**
     * Update product images
     */
    updateImages(productCard, selectedDot) {
      const variantImageUrl = selectedDot.getAttribute("data-variant-image");
      const variantImageSrcset = selectedDot.getAttribute("data-variant-image-srcset");
      const variantImageSizes = selectedDot.getAttribute("data-variant-image-sizes");

      const variantImageMobileUrl = selectedDot.getAttribute("data-variant-image-mobile");
      const variantImageMobileSrcset = selectedDot.getAttribute("data-variant-image-mobile-srcset");

      const variantFlavorValue = selectedDot.getAttribute("data-variant-flavor-value");

      const imageWrapper = productCard.querySelector("[data-product-image-wrapper]");
      if (!imageWrapper) return;

      // Check for dual images (mobile + desktop)
      const mobileImg = imageWrapper.querySelector(".product-slider-img--mobile");
      const desktopImg = imageWrapper.querySelector(".product-slider-img--desktop");

      // Get product images for alt-text matching fallback
      const productImages = this.getProductImages(productCard);

      if (mobileImg && desktopImg) {
        // Handle Mobile Image Update with fallback to desktop variant image
        let targetMobileUrl = variantImageMobileUrl || variantImageUrl;
        let targetMobileSrcset = variantImageMobileSrcset || variantImageSrcset;

        if (targetMobileUrl) {
          this.updateImageWithFade(mobileImg, targetMobileUrl, targetMobileSrcset, variantImageSizes);
        }

        // Handle Desktop Image Update
        if (variantImageUrl) {
          this.updateImageWithFade(desktopImg, variantImageUrl, variantImageSrcset, variantImageSizes);
        } else if (variantFlavorValue) {
          const matchedImage = this.findImageByAltText(productImages, variantFlavorValue, true);
          if (matchedImage) {
            this.updateImageWithFade(desktopImg, matchedImage.src, matchedImage.srcset, variantImageSizes);
          }
        }
      } else {
        // Fallback to single image update (standard behavior)
        const img = imageWrapper.querySelector("img");
        if (!img) return;

        let targetImageUrl = variantImageUrl;
        let targetImageSrcset = variantImageSrcset;
        let targetImageSizes = variantImageSizes;

        if (!targetImageUrl && variantFlavorValue) {
          const matchedImage = this.findImageByAltText(productImages, variantFlavorValue, true);
          if (matchedImage) {
            targetImageUrl = matchedImage.src;
            targetImageSrcset = matchedImage.srcset || "";
          }
        }

        if (!targetImageUrl && productImages.length > 0) {
          targetImageUrl = productImages[0].src;
          targetImageSrcset = productImages[0].srcset || "";
        }

        if (targetImageUrl) {
          this.updateImageWithFade(img, targetImageUrl, targetImageSrcset, targetImageSizes);
        }
      }

      // Keep theme's native hover behavior intact
      this.updateSecondaryImage(productCard, selectedDot, variantFlavorValue, productImages);
    }

    /**
     * Update image with smooth fade transition
     */
    updateImageWithFade(img, newSrc, newSrcset, newSizes) {
      if (!img || (!newSrc && !newSrcset)) return;

      // Add fade class if not already present
      if (!img.classList.contains(FADE_CLASS)) {
        img.classList.add(FADE_CLASS);
      }

      // Create new image element for preloading
      const newImg = new Image();

      // If we have a srcset, set it on the preloader too so the browser can load the right size
      if (newSrcset) {
        newImg.srcset = newSrcset;
      }
      if (newSizes) {
        newImg.sizes = newSizes;
      }
      
      newImg.onload = () => {
        // Trigger fade effect by temporarily reducing opacity
        img.style.transition = `opacity ${TRANSITION_DURATION}ms ease-in-out`;
        img.style.opacity = "0";

        // Wait for opacity to reach 0 before swapping sources
        setTimeout(() => {
          // Clear current srcset first to force re-evaluation on some browsers
          img.removeAttribute('srcset');
          
          // Update image source, srcset and sizes
          if (newSrcset) img.setAttribute('srcset', newSrcset);
          if (newSizes) img.setAttribute('sizes', newSizes);
          if (newSrc) img.setAttribute('src', newSrc);

          // Fade back in
          img.style.opacity = "1";

          // Clean up transition after it completes
          setTimeout(() => {
            img.style.transition = "";
            img.style.opacity = "";
          }, TRANSITION_DURATION);
        }, TRANSITION_DURATION / 2);
      };

      newImg.onerror = () => {
        console.warn("FlavorDotSwap: Failed to preload image", newSrc);
      };

      // Start loading
      if (newSrc) newImg.src = newSrc;
    }

    /**
     * Update secondary (hover) image
     */
    updateSecondaryImage(productCard, selectedDot, variantFlavorValue, productImages) {
      const secondaryWrapper = productCard.querySelector("[data-product-secondary-image-wrapper]");
      if (!secondaryWrapper) return;

      const img = secondaryWrapper.querySelector("img");
      if (!img) return;

      // Check for variant-level custom hover image first
      const variantHoverImageUrl = selectedDot.getAttribute("data-variant-hover-image");
      const variantHoverImageSrcset = selectedDot.getAttribute("data-variant-hover-image-srcset");

      if (variantHoverImageUrl) {
        // Use variant hover image if available
        img.src = variantHoverImageUrl;
        if (variantHoverImageSrcset) {
          img.srcset = variantHoverImageSrcset;
        }
        return;
      }

      // Fallback to finding image by alt text or second product image
      let targetImage = null;

      // Find second closest image by alt text
      if (variantFlavorValue) {
        targetImage = this.findImageByAltText(productImages, variantFlavorValue, false);
      }

      // Fallback to second product image
      if (!targetImage && productImages.length > 1) {
        targetImage = productImages[1];
      }

      if (targetImage && targetImage.src) {
        // Update immediately without fade for secondary image (it's hidden until hover)
        // This ensures it's ready when user hovers
        if (targetImage.src) img.src = targetImage.src;
        if (targetImage.srcset) img.srcset = targetImage.srcset;
      }
    }

    /**
     * Get all product images from the card
     */
    getProductImages(productCard) {
      const images = [];
      const imageElements = productCard.querySelectorAll("img[src]");

      imageElements.forEach((img) => {
        if (img.src && !img.src.includes("placeholder")) {
          images.push({
            src: img.src,
            srcset: img.srcset || "",
            alt: img.alt || "",
          });
        }
      });

      return images;
    }

    /**
     * Find image by matching alt text with flavor value
     * @param {Array} images - Array of image objects with src, alt properties
     * @param {string} flavorValue - Flavor value to match
     * @param {boolean} firstMatch - If true, return first match; if false, return second match
     */
    findImageByAltText(images, flavorValue, firstMatch) {
      if (!flavorValue || !images.length) return null;

      const lowerFlavorValue = flavorValue.toLowerCase().trim();
      const matches = [];

      images.forEach((image) => {
        const alt = (image.alt || "").toLowerCase();
        // Partial match: "Lemon" matches "Lemon - Einzelpackung"
        if (alt.includes(lowerFlavorValue) || lowerFlavorValue.includes(alt.split(" ")[0])) {
          matches.push(image);
        }
      });

      if (firstMatch) {
        return matches[0] || null;
      } else {
        // Return second match for secondary image
        return matches[1] || matches[0] || null;
      }
    }

    /**
     * Update product price
     */
    updatePrice(productCard, selectedDot) {
      const variantPrice = selectedDot.getAttribute("data-variant-price");
      const variantComparePrice = selectedDot.getAttribute("data-variant-compare-price");
      const variantUnitPrice = selectedDot.getAttribute("data-variant-unit-price");

      if (!variantPrice) return;

      // Update main price and preserve any prefix (e.g., "ab" / "from") that the theme shows for ranged pricing
      const priceElement = productCard.querySelector("[data-variant-price-element]");
      if (priceElement) {
        const originalText = priceElement.textContent || "";
        // Try to capture a non-numeric prefix at the start (e.g., "ab", "from", localized words)
        const prefixMatch = originalText.match(/^\s*([^0-9€$£]*)/); // stops before first digit or currency symbol
        const prefixRaw = prefixMatch && prefixMatch[1] ? prefixMatch[1].trim() : "";
        const prefix = prefixRaw ? `${prefixRaw} ` : "";
        priceElement.textContent = `${prefix}${variantPrice}`.trim();

        // Update sale price class if applicable
        if (variantComparePrice) {
          priceElement.classList.add("sale-price");
        } else {
          priceElement.classList.remove("sale-price");
        }
      }

      // Update compare-at price if on sale
      const comparePriceElement = productCard.querySelector(".grid-product__price--original");
      if (comparePriceElement) {
        if (variantComparePrice) {
          comparePriceElement.textContent = variantComparePrice;
          comparePriceElement.style.display = "";
        } else {
          comparePriceElement.style.display = "none";
        }
      }

      // Update unit price
      const unitPriceElement = productCard.querySelector("[data-variant-unit-price-element]");
      if (unitPriceElement) {
        if (variantUnitPrice) {
          unitPriceElement.textContent = variantUnitPrice;
          unitPriceElement.style.display = "";
        } else {
          unitPriceElement.style.display = "none";
        }
      }
    }

    /**
     * Update product badges
     */
    updateBadges(productCard, selectedDot) {
      const badgesContainer = productCard.querySelector("[data-variant-badges-container]");
      if (!badgesContainer) return;

      const badge1 = selectedDot.getAttribute("data-variant-badge-1");
      const badge2 = selectedDot.getAttribute("data-variant-badge-2");
      const badge3 = selectedDot.getAttribute("data-variant-badge-3");

      // Parse badge JSON data
      let badges = [];
      try {
        if (badge1) badges.push(JSON.parse(badge1));
        if (badge2) badges.push(JSON.parse(badge2));
        if (badge3) badges.push(JSON.parse(badge3));
      } catch (e) {
        console.warn("Error parsing badge data:", e);
      }

      // If variant has badges, use them; otherwise keep existing badges
      if (badges.length > 0) {
        this.renderBadges(badgesContainer, badges);
      }
      // If no variant badges, badges container keeps its original content (product-level badges)
    }

    /**
     * Render badges HTML
     */
    renderBadges(container, badges) {
      // Clear existing badges
      container.innerHTML = "";

      badges.forEach((badge, index) => {
        if (!badge || !badge.text) return;

        const badgeClasses = index === 0 ? "badge bf" : index === 1 ? "badge save" : "badge custom";
        const enableBorder = badge.enable_border === true || badge.enable_border === "true";

        const badgeEl = document.createElement("div");
        badgeEl.className = badgeClasses;
        badgeEl.textContent = badge.text;

        let style = `background: ${badge.color || ""};`;
        if (badge.text_color) style += ` color: ${badge.text_color};`;
        if (enableBorder && badge.border_color) {
          style += ` border: 1px solid ${badge.border_color};`;
        }

        badgeEl.setAttribute("style", style);
        container.appendChild(badgeEl);
      });
    }

    /**
     * Update product link URL
     */
    updateProductUrl(productCard, selectedDot) {
      const variantUrl = selectedDot.getAttribute("data-variant-url");
      if (!variantUrl) return;

      const productLink = productCard.querySelector(".grid-product__link");
      if (productLink) {
        productLink.setAttribute("href", variantUrl);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      new FlavorDotSwap();
    });
  } else {
    new FlavorDotSwap();
  }

  // Re-initialize for dynamic content (theme editor, AJAX loads)
  if (typeof document.addEventListener !== "undefined") {
    document.addEventListener("shopify:section:load", () => {
      // Section was reloaded, but event delegation should still work
      // No need to re-initialize as we use event delegation
    });
  }
})();
